const LANGUAGE_LOCALE_MAP = {
  en: 'en-US',
  it: 'it-IT',
  es: 'es-ES',
  fr: 'fr-FR',
  de: 'de-DE',
  pt: 'pt-PT',
  tr: 'tr-TR'
};

const DEFAULT_LANGUAGE = 'it';

export const normalizeLanguage = (language) => {
  if (!language || typeof language !== 'string') {
    return DEFAULT_LANGUAGE;
  }
  const normalized = language.trim().toLowerCase();
  return LANGUAGE_LOCALE_MAP[normalized] ? normalized : DEFAULT_LANGUAGE;
};

export const getStoredLanguage = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  const storedLanguage = window.localStorage.getItem('language');
  if (storedLanguage) {
    return normalizeLanguage(storedLanguage);
  }

  try {
    const user = JSON.parse(window.localStorage.getItem('user') || 'null');
    return normalizeLanguage(user?.language);
  } catch {
    return DEFAULT_LANGUAGE;
  }
};

export const setStoredLanguage = (language) => {
  if (typeof window === 'undefined') {
    return;
  }
  const normalized = normalizeLanguage(language);
  window.localStorage.setItem('language', normalized);
  if (document?.documentElement) {
    document.documentElement.lang = normalized;
  }
};

export const getLocale = (language) => {
  const normalized = normalizeLanguage(language || getStoredLanguage());
  return LANGUAGE_LOCALE_MAP[normalized] || LANGUAGE_LOCALE_MAP[DEFAULT_LANGUAGE];
};

const hasExplicitDateFields = (options) => {
  if (!options || typeof options !== 'object') return false;
  return [
    'weekday',
    'year',
    'month',
    'day',
    'era'
  ].some((key) => options[key] !== undefined);
};

const hasExplicitTimeFields = (options) => {
  if (!options || typeof options !== 'object') return false;
  return [
    'hour',
    'minute',
    'second',
    'timeZoneName'
  ].some((key) => options[key] !== undefined);
};

export const formatDate = (value, options = {}, overrideLocale) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const locale = overrideLocale || getLocale();
  const useDateStyle = !hasExplicitDateFields(options) && options.dateStyle === undefined;
  const formatter = new Intl.DateTimeFormat(locale, {
    ...(useDateStyle ? { dateStyle: 'medium' } : {}),
    ...options
  });
  return formatter.format(date);
};

export const formatDateTime = (value, options = {}, overrideLocale) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const locale = overrideLocale || getLocale();
  const useDateStyle = !hasExplicitDateFields(options) && options.dateStyle === undefined;
  const useTimeStyle = !hasExplicitTimeFields(options) && options.timeStyle === undefined;
  const formatter = new Intl.DateTimeFormat(locale, {
    ...(useDateStyle ? { dateStyle: 'medium' } : {}),
    ...(useTimeStyle ? { timeStyle: 'short' } : {}),
    ...options
  });
  return formatter.format(date);
};

export const formatNumber = (value, options = {}, overrideLocale) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '0';
  }
  const locale = overrideLocale || getLocale();
  return new Intl.NumberFormat(locale, options).format(numeric);
};

export const formatCurrency = (value, currency = 'EUR', options = {}, overrideLocale) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return formatNumber(0, options, overrideLocale);
  }
  const locale = overrideLocale || getLocale();
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    ...options
  }).format(numeric);
};
